import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Check, ChevronDown } from 'lucide-react';
import { formatMad } from '../../data/servers';

// ---------- Thèmes ----------
export const THEMES = {
  buy: {
    accent: 'yellow',
    pill: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
    catActive: 'bg-yellow-500 text-black',
    icon: 'text-yellow-400',
    focus: 'focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500',
    button: 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black',
    card: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30',
    price: 'text-yellow-400',
  },
  sell: {
    accent: 'green',
    pill: 'bg-green-500/10 border-green-500/30 text-green-400',
    catActive: 'bg-green-500 text-black',
    icon: 'text-green-400',
    focus: 'focus:border-green-500 focus:ring-1 focus:ring-green-500',
    button: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-black',
    card: 'from-green-500/20 to-green-600/10 border-green-500/30',
    price: 'text-green-400',
  },
};

// ---------- Champ texte avec erreur ----------
export const Field = ({ label, name, value, onChange, error, required, icon: Icon, theme, hint, ...rest }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-300 mb-2">
      {label}{required && <span className="text-red-400"> *</span>}
    </label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />}
      <input
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`w-full bg-gray-950 border rounded-lg py-2 sm:py-3 px-4 ${Icon ? 'pl-10' : ''} text-white placeholder-gray-500 ${theme.focus} outline-none text-sm ${
          error ? 'border-red-500' : 'border-gray-700'
        }`}
        {...rest}
      />
    </div>
    {error
      ? <p id={`${name}-error`} role="alert" className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={12} />{error}</p>
      : hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

// ---------- Liste déroulante (clic dehors / Échap / clavier) ----------
export const SelectDropdown = ({ label, value, options, onSelect, icon: Icon, theme, renderOption }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={`w-full bg-gray-950 border rounded-lg py-3 px-4 flex items-center justify-between text-left text-sm ${open ? 'border-yellow-500/60' : 'border-gray-700'}`}
      >
        <span className="flex items-center gap-2 min-w-0">
          {Icon && <Icon size={18} className={`${theme.icon} flex-shrink-0`} />}
          <span className="truncate">{value || 'Choisir…'}</span>
        </span>
        <ChevronDown size={18} className={`text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            className="absolute z-20 w-full mt-2 bg-gray-800 border border-gray-700 rounded-lg max-h-48 overflow-y-auto shadow-xl"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {options.map(opt => (
              <li key={opt.value} role="option" aria-selected={opt.value === value}>
                <button
                  type="button"
                  onClick={() => { onSelect(opt.value); setOpen(false); }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-700 transition-colors border-b border-gray-700 last:border-0"
                >
                  {renderOption ? renderOption(opt) : opt.value}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------- Choix de catégorie ----------
export const CategoryPicker = ({ categories, value, onChange, theme }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">Catégorie</label>
    <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Catégorie de serveur">
      {categories.map(cat => (
        <button
          key={cat}
          type="button"
          role="radio"
          aria-checked={value === cat}
          onClick={() => onChange(cat)}
          className={`py-2 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${
            value === cat ? theme.catActive : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          {cat.replace('DOFUS ', 'D.')}
        </button>
      ))}
    </div>
  </div>
);

// ---------- Quantité ----------
export const QuantityStepper = ({ value, onChange, error }) => (
  <div>
    <label htmlFor="quantity" className="block text-sm font-medium text-gray-300 mb-2">Quantité (Millions)</label>
    <div className="flex items-center gap-2 sm:gap-4">
      <button
        type="button"
        aria-label="Diminuer la quantité"
        onClick={() => onChange(-1)}
        className="w-10 h-10 flex-shrink-0 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center text-lg sm:text-xl"
      >
        −
      </button>
      <input
        id="quantity"
        type="number"
        name="quantity"
        inputMode="numeric"
        min="1"
        max="50000"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        className={`flex-1 min-w-0 bg-gray-950 border rounded-lg py-2 sm:py-3 text-center text-lg sm:text-xl font-bold outline-none ${
          error ? 'border-red-500' : 'border-gray-700'
        }`}
      />
      <button
        type="button"
        aria-label="Augmenter la quantité"
        onClick={() => onChange(1)}
        className="w-10 h-10 flex-shrink-0 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center text-lg sm:text-xl"
      >
        +
      </button>
    </div>
    {error && <p role="alert" className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
  </div>
);

// ---------- Récapitulatif ----------
export const SummaryCard = ({ theme, title, icon: Icon, server, quantity, pricePerM, total, totalLabel, points, extra }) => (
  <div className={`bg-gradient-to-br ${theme.card} border rounded-2xl p-4 sm:p-6`}>
    <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-6 flex items-center gap-2">
      <Icon className={theme.icon} />
      {title}
    </h3>

    <div className="space-y-2 sm:space-y-4 mb-4 sm:mb-6 text-sm sm:text-base">
      <div className="flex justify-between text-gray-400 gap-2">
        <span>Serveur</span>
        <span className="text-white font-medium text-right">{server || '—'}</span>
      </div>
      <div className="flex justify-between text-gray-400">
        <span>Quantité</span>
        <span className="text-white font-medium">{quantity}M</span>
      </div>
      <div className="flex justify-between text-gray-400">
        <span>Prix par million</span>
        <span className="text-white font-medium">{formatMad(pricePerM)}</span>
      </div>
      <div className="h-px bg-gray-700" />
      <div className="flex justify-between items-center gap-2">
        <span className="text-lg font-medium">{totalLabel}</span>
        <span className={`text-2xl sm:text-3xl font-bold ${theme.price} whitespace-nowrap`}>{formatMad(total)}</span>
      </div>
    </div>

    <div className="bg-black/40 rounded-xl p-3 sm:p-4 grid grid-cols-2 md:grid-cols-1 gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-400">
      {points.map(p => (
        <div key={p} className="flex items-center gap-2">
          <Check size={14} className="text-green-400 flex-shrink-0" />
          <span>{p}</span>
        </div>
      ))}
    </div>

    {extra}
  </div>
);

// ---------- Bandeau d'erreur global ----------
export const ErrorBanner = ({ message }) => message ? (
  <motion.div
    role="alert"
    className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-center gap-2"
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <AlertCircle size={18} className="flex-shrink-0" />
    {message}
  </motion.div>
) : null;

// ---------- Écran de succès ----------
export const SuccessScreen = ({ title, lines }) => (
  <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-4">
    <motion.div
      className="text-center max-w-md"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <motion.div
        className="w-20 h-20 bg-green-500/20 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', delay: 0.2 }}
      >
        <Check className="w-10 h-10 text-green-500" />
      </motion.div>
      <h2 className="text-2xl font-bold mb-2">{title}</h2>
      {lines.map((line, i) => (
        <p key={i} className={i === lines.length - 1 ? 'text-yellow-400' : 'text-gray-400 mb-2 text-sm sm:text-base'}>{line}</p>
      ))}
    </motion.div>
  </div>
);
